import { ref } from "vue";
import type { Patient } from "@virtual-care/shared";
import { callApiClient } from "../services/callApiClient";
import { signalingService } from "../services/signalingService";
import { useCallStore } from "../store/callStore";
import { usePatientStore } from "../store/patientStore";

export function usePatientActions() {
  const callDialogOpen = ref(false);
  const selectedPatientId = ref<string | null>(null);
  const selectedRoomId = ref<string | null>(null);
  const shouldShowBreakthrough = ref(false);
  const isCreatingCall = ref(false); // Prevent multiple simultaneous calls

  const initiateCall = (patient: Patient) => {
    selectedPatientId.value = patient.patientId;
    selectedRoomId.value = patient.room.roomId;
    callDialogOpen.value = true;
    shouldShowBreakthrough.value = false;
  };

  const handleBreakthrough = (patient: Patient) => {
    selectedPatientId.value = patient.patientId;
    selectedRoomId.value = patient.room.roomId;
    callDialogOpen.value = true;
    shouldShowBreakthrough.value = true;
  };

  const closeCallDialog = () => {
    callDialogOpen.value = false;
    selectedPatientId.value = null;
    selectedRoomId.value = null;
    shouldShowBreakthrough.value = false;
  };

  const confirmCall = async () => {
    console.log('[usePatientActions] confirmCall called');
    
    // Prevent multiple simultaneous call creation attempts.
    // This guards against race conditions where:
    // 1. User rapidly clicks "Call" multiple times
    // 2. Dialog watch triggers multiple times due to reactive state changes
    // 3. Previous call is cancelled but new call starts before DB update completes
    if (isCreatingCall.value) {
      console.warn('[usePatientActions] Call creation already in progress, ignoring duplicate request');
      return;
    }
    
    const roomId = selectedRoomId.value;
    const patientId = selectedPatientId.value;
    console.log('[usePatientActions] roomId:', roomId, 'patientId:', patientId);
    if (!roomId) {
      console.error("[usePatientActions] confirmCall: no roomId set");
      return;
    }

    const callStore = useCallStore();
    const patientStore = usePatientStore();

    // Set flag to prevent duplicate calls
    isCreatingCall.value = true;

    try {
      console.log('[usePatientActions] Calling createSession API...', { roomId });
      const sessionResponse = await callApiClient.createSession(roomId);
      console.log('[usePatientActions] Session created:', sessionResponse);
      const sessionId = sessionResponse.sessionId;
      const provider = sessionResponse.provider;

      // Store sessionId immediately so CallDialog can cancel if needed
      callStore.setSession(sessionId, roomId, provider, undefined);
      console.log('[usePatientActions] SessionId stored in callStore:', sessionId);

      // Optimistically mark this patient's call as initiated so the STATUS
      // column shows "Calling…" immediately without waiting for a page reload.
      if (patientId) {
        const patient = patientStore.patients.find((p) => p.patientId === patientId);
        if (patient) {
          const now = new Date().toISOString();
          patient.recentCallLog = {
            patientId,
            callId: sessionId,
            sessionId,
            initiatorId: "",
            participants: [],
            startTime: now,
            status: "initiated",
            createdAt: now,
            updatedAt: now,
          };
        }
      }

      const joinResponse = await callApiClient.joinSession(sessionId, "nurse");
      const credentials = joinResponse.session?.credentials;

      // Update with credentials after joining
      callStore.setSession(sessionId, roomId, provider, credentials);
      console.log('[usePatientActions] Credentials stored in callStore');

      // Join the socket.io session room so the nurse receives call_accepted / call_declined / call_ended
      signalingService.joinSession(sessionId);

      // Confirm optimistic update with server data in the background
      if (patientId) {
        patientStore.refreshPatientCallLog(patientId).catch(() => {});
      }
    } catch (error) {
      console.error("[usePatientActions] confirmCall failed:", error);
      callDialogOpen.value = false;
    } finally {
      // Reset flag to allow future calls
      isCreatingCall.value = false;
    }
  };

  return {
    callDialogOpen,
    selectedPatientId,
    selectedRoomId,
    shouldShowBreakthrough,
    initiateCall,
    handleBreakthrough,
    closeCallDialog,
    confirmCall,
  };
}
